import { useState } from "react";
import Header from "./Header";

const API = "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod";

// ── SheetJS CDN loader ────────────────────────────────────────────────────────
let xlsxLib = null;
const loadXLSX = () =>
  new Promise((resolve, reject) => {
    if (xlsxLib) return resolve(xlsxLib);
    if (window.XLSX) { xlsxLib = window.XLSX; return resolve(xlsxLib); }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    script.onload = () => { xlsxLib = window.XLSX; resolve(xlsxLib); };
    script.onerror = () => reject(new Error("Failed to load SheetJS"));
    document.head.appendChild(script);
  });

// ── Auth header ───────────────────────────────────────────────────────────────
const authHeader = () => ({ Authorization: localStorage.getItem("SLNAdminToken") ?? "" });

// ── Fetch helpers ─────────────────────────────────────────────────────────────
const fetchCourses = async () => {
  const res = await fetch(`${API}/Courses/GetAllCourses?isActive=true`, { headers: authHeader() });
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data.data ?? []);
  return list.map((c) => ({
    "Course ID":        c.courseId ?? "",
    "Title":            c.title ?? "",
    "Category":         c.category ?? "",
    "Original Price":   c.originalPrice ?? "",
    "Offered Price":    c.offeredPrice ?? "",
    "Instructor":       c.instructors?.[0]?.name ?? "",
    "Rating":           c.instructors?.[0]?.rating ?? "",
    "Lessons":          c.lessons?.length ?? 0,
    "Is Active":        c.isActive ? "Yes" : "No",
    "Created At":       c.createdAt ? new Date(c.createdAt).toLocaleString("en-IN") : "",
    "Updated At":       c.updatedAt ? new Date(c.updatedAt).toLocaleString("en-IN") : "",
  }));
};

const fetchSymbols = async () => {
  const res = await fetch(`${API}/GetAllSymbolsOrHolidays?type=symbol`, { headers: authHeader() });
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data.symbols ?? data.data ?? []);
  return list.map((s) => ({
    "Symbol":   s.symbol ?? s,
    "Exchange": s.exchange ?? s.Exchange ?? (typeof s === "string" ? s.split(":")?.[0] : ""),
    "Created At": s.createdAt ? new Date(s.createdAt).toLocaleString("en-IN") : "",
  }));
};

const fetchHolidays = async () => {
  const res = await fetch(`${API}/GetAllSymbolsOrHolidays?type=holiday`, { headers: authHeader() });
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data.holidays ?? data.data ?? []);
  return list.map((h) => ({
    "Date":     h.date ?? "",
    "Day":      h.date ? new Date(h.date).toLocaleDateString("en-IN", { weekday: "long" }) : "",
    "Exchange": h.exchange ?? "",
    "Reason":   h.reason ?? "",
  }));
};

const fetchUsers = async () => {
  const res = await fetch(`${API}/Profile/GetUserProfile`, { headers: authHeader() });
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data.data ?? data.users ?? []);
  return list.map((u) => ({
    "User ID":            u.userId ?? "",
    "Name":               u.name ?? "",
    "Email":              u.email ?? "",
    "Phone":              u.phone ?? "",
    "Status":             u.status ?? "",
    "SLN Access":         u.slnAccess ? "Yes" : "No",
    "Phone Verified":     u.isPhoneVerified ? "Yes" : "No",
    "Email Verified":     u.isEmailVerified ? "Yes" : "No",
    "First Payment Done": u.firstPaymentDone ? "Yes" : "No",
    "Referral Code":      u.referralCode ?? "",
    "Referred By":        u.referredBy ?? "",
    "Joined At":          u.createdAt ? new Date(u.createdAt).toLocaleString("en-IN") : "",
    "Updated At":         u.updatedAt ? new Date(u.updatedAt).toLocaleString("en-IN") : "",
  }));
};

const fetchPayments = async () => {
  const res = await fetch(`${API}/Payment/GetPaymentHistory`, { headers: authHeader() });
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data.payments ?? data.data ?? []);
  return list.map((p) => ({
    "Payment ID":  p.paymentId ?? p.payment_id ?? p.razorpay_payment_id ?? "",
    "Order ID":    p.orderId ?? p.order_id ?? "",
    "User ID":     p.userId ?? "",
    "User Name":   p.userName ?? p.name ?? "",
    "Email":       p.userEmail ?? p.email ?? "",
    "Phone":       p.userPhone ?? p.phone ?? "",
    "Course ID":   p.courseId ?? "",
    "Course":      p.courseTitle ?? "",
    "Amount (₹)":  p.amount != null ? (p.amount / 100).toFixed(2) : "",
    "Currency":    p.currency ?? "INR",
    "Status":      p.status ?? "",
    "Method":      p.method ?? p.payment_method ?? "",
    "Coupon":      p.couponCode ?? p.coupon_code ?? "",
    "Discount":    p.discount ?? p.discount_amount ?? "",
    "Date":        p.createdAt ? new Date(p.createdAt).toLocaleString("en-IN") : "",
  }));
};

const fetchCoupons = async () => {
  const res = await fetch(`${API}/Coupon/GetAllCoupon`, { headers: authHeader() });
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data.coupons ?? data.data ?? []);
  return list.map((c) => ({
    "Code":            c.code ?? "",
    "Description":     c.description ?? "",
    "Discount Type":   c.discount_type ?? "",
    "Discount Value":  c.discount_value ?? "",
    "Min Purchase":    c.min_purchase_amount ?? "",
    "Max Discount":    c.max_discount_amount ?? "",
    "Usage Count":     c.usage_count ?? 0,
    "Usage Limit":     c.usage_limit ?? "",
    "Status":          c.status ?? "",
    "Start Date":      c.start_date ? new Date(c.start_date).toLocaleDateString("en-IN") : "",
    "End Date":        c.end_date ? new Date(c.end_date).toLocaleDateString("en-IN") : "",
    "Created At":      c.created_at ? new Date(c.created_at).toLocaleString("en-IN") : "",
  }));
};

// ── Report config ─────────────────────────────────────────────────────────────
const REPORTS = [
  {
    id: "courses",
    label: "Courses",
    description: "All active courses with pricing, instructor & lesson details",
    icon: "bi-journal-bookmark-fill",
    color: "text-primary",
    bg: "bg-primary-subtle",
    border: "border-primary-subtle",
    fetcher: fetchCourses,
    filename: "Courses_Report",
  },
  {
    id: "symbols",
    label: "Symbols",
    description: "All trading symbols with exchange info",
    icon: "bi-graph-up-arrow",
    color: "text-success",
    bg: "bg-success-subtle",
    border: "border-success-subtle",
    fetcher: fetchSymbols,
    filename: "Symbols_Report",
  },
  {
    id: "holidays",
    label: "Market Holidays",
    description: "NSE / BSE / MCX exchange holidays with dates & reasons",
    icon: "bi-calendar-x-fill",
    color: "text-warning",
    bg: "bg-warning-subtle",
    border: "border-warning-subtle",
    fetcher: fetchHolidays,
    filename: "MarketHolidays_Report",
  },
  {
    id: "users",
    label: "Users",
    description: "All registered users with verification & access status",
    icon: "bi-people-fill",
    color: "text-info",
    bg: "bg-info-subtle",
    border: "border-info-subtle",
    fetcher: fetchUsers,
    filename: "Users_Report",
  },
  {
    id: "payments",
    label: "Payments",
    description: "Full payment history with amounts, status & user details",
    icon: "bi-credit-card-fill",
    color: "text-danger",
    bg: "bg-danger-subtle",
    border: "border-danger-subtle",
    fetcher: fetchPayments,
    filename: "Payments_Report",
  },
  {
    id: "coupons",
    label: "Coupons",
    description: "All discount coupons with usage stats & validity",
    icon: "bi-ticket-perforated-fill",
    color: "text-purple",
    bg: "bg-secondary-subtle",
    border: "border-secondary-subtle",
    fetcher: fetchCoupons,
    filename: "Coupons_Report",
  },
];

// ── Download helpers ──────────────────────────────────────────────────────────
const downloadExcel = (XLSX, rows, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);

  // Auto column widths
  const cols = Object.keys(rows[0] ?? {});
  ws["!cols"] = cols.map((key) => ({
    wch: Math.max(key.length, ...rows.map((r) => String(r[key] ?? "").length), 10) + 2,
  }));

  // Header row style (bold + fill)
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (!cell) continue;
    cell.s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { patternType: "solid", fgColor: { rgb: "1E3A5F" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left:   { style: "thin", color: { rgb: "CCCCCC" } },
        right:  { style: "thin", color: { rgb: "CCCCCC" } },
      },
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  const ts = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${filename}_${ts}.xlsx`);
};

const downloadCSV = (XLSX, rows, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const ts = new Date().toISOString().slice(0, 10);
  a.download = `${filename}_${ts}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Component ─────────────────────────────────────────────────────────────────
function Reports() {
  // loading state per report id, format state per report id
  const [loading, setLoading] = useState({});    // { id: true/false }
  const [lastCount, setLastCount] = useState({}); // { id: number }
  const [error, setError] = useState({});         // { id: string }

  const handleDownload = async (report, format) => {
    const key = `${report.id}_${format}`;
    setLoading((p) => ({ ...p, [key]: true }));
    setError((p) => ({ ...p, [report.id]: "" }));

    try {
      const [XLSX, rows] = await Promise.all([loadXLSX(), report.fetcher()]);
      console.log(`📦 ${report.label} data (${rows.length} rows):`, rows);

      if (!rows.length) {
        setError((p) => ({ ...p, [report.id]: "No data found to export." }));
        return;
      }

      setLastCount((p) => ({ ...p, [report.id]: rows.length }));

      if (format === "xlsx") downloadExcel(XLSX, rows, report.filename);
      else downloadCSV(XLSX, rows, report.filename);
    } catch (err) {
      console.error(`❌ ${report.label} export error:`, err);
      setError((p) => ({ ...p, [report.id]: err.message }));
    } finally {
      setLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const isLoading = (id, fmt) => loading[`${id}_${fmt}`];
  const anyLoading = (id) => isLoading(id, "xlsx") || isLoading(id, "csv");

  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          {/* ── Page Header ── */}
          <div className="row">
            <div className="col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">Reports</h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Download data exports as Excel (.xlsx) or CSV
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Report Cards ── */}
          <div className="row g-2">
            {REPORTS.map((report) => (
              <div key={report.id} className="col-lg-4 col-md-6 col-12">
                <div className={`border ${report.border} rounded-4 p-4 h-100 bg-white`}>
                  {/* Icon + Label */}
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      className={`${report.bg} ${report.border} border rounded-3 d-flex align-items-center justify-content-center`}
                      style={{ width: 48, height: 48, flexShrink: 0 }}
                    >
                      <i className={`bi ${report.icon} fs-4 ${report.color}`}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold text-dark">{report.label}</h6>
                      {lastCount[report.id] != null && (
                        <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle small px-2 rounded-3 mt-1">
                          {lastCount[report.id]} rows last export
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-secondary small mb-3 lh-sm">
                    {report.description}
                  </p>

                  {/* Error */}
                  {error[report.id] && (
                    <div className="alert alert-danger rounded-3 py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                      <i className="bi bi-exclamation-triangle-fill flex-shrink-0"></i>
                      {error[report.id]}
                    </div>
                  )}

                  {/* Download buttons */}
                  <div className="d-flex gap-2">
                    {/* Excel */}
                    <button
                      className="btn btn-sm btn-success fw-normal px-3 rounded-3 d-inline-flex align-items-center gap-2 flex-grow-1 justify-content-center"
                      onClick={() => handleDownload(report, "xlsx")}
                      disabled={anyLoading(report.id)}
                    >
                      {isLoading(report.id, "xlsx") ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" />
                          Fetching…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-excel-fill"></i>
                          Excel
                        </>
                      )}
                    </button>

                    {/* CSV */}
                    <button
                      className="btn btn-sm btn-outline-secondary fw-normal px-3 rounded-3 d-inline-flex align-items-center gap-2 flex-grow-1 justify-content-center"
                      onClick={() => handleDownload(report, "csv")}
                      disabled={anyLoading(report.id)}
                    >
                      {isLoading(report.id, "csv") ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" />
                          Fetching…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-filetype-csv"></i>
                          CSV
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Download All ── */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="border rounded-4 p-4 bg-light">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                  <div>
                    <h6 className="mb-0 fw-semibold text-dark d-flex align-items-center gap-2">
                      <i className="bi bi-archive-fill text-primary"></i>
                      Download All Reports
                    </h6>
                    <p className="mb-0 text-secondary small">
                      Exports all 6 data sources into a single Excel workbook with separate sheets.
                    </p>
                  </div>
                  <DownloadAllButton
                    loading={loading}
                    setLoading={setLoading}
                    setLastCount={setLastCount}
                    setError={setError}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ── Download All Button ───────────────────────────────────────────────────────
function DownloadAllButton({ loading, setLoading, setLastCount, setError }) {
  const key = "all_xlsx";
  const isLoading = loading[key];

  const handleAll = async () => {
    setLoading((p) => ({ ...p, [key]: true }));

    try {
      const XLSX = await loadXLSX();
      const wb = XLSX.utils.book_new();

      const results = await Promise.allSettled(
        REPORTS.map((r) => r.fetcher().then((rows) => ({ report: r, rows })))
      );

      let anyData = false;
      results.forEach((result) => {
        if (result.status === "fulfilled") {
          const { report, rows } = result.value;
          if (!rows.length) return;
          anyData = true;

          const ws = XLSX.utils.json_to_sheet(rows);
          const cols = Object.keys(rows[0] ?? {});
          ws["!cols"] = cols.map((key) => ({
            wch: Math.max(key.length, ...rows.map((r) => String(r[key] ?? "").length), 10) + 2,
          }));

          // Header styling
          const range = XLSX.utils.decode_range(ws["!ref"]);
          for (let C = range.s.c; C <= range.e.c; C++) {
            const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
            if (!cell) continue;
            cell.s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { patternType: "solid", fgColor: { rgb: "1E3A5F" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          }

          XLSX.utils.book_append_sheet(wb, ws, report.label);
          setLastCount((p) => ({ ...p, [report.id]: rows.length }));
          console.log(`📦 ${report.label}: ${rows.length} rows`);
        } else {
          console.error(`❌ ${result.reason}`);
        }
      });

      if (!anyData) {
        setError((p) => ({ ...p, all: "No data found in any report." }));
        return;
      }

      const ts = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `All_Reports_${ts}.xlsx`);
    } catch (err) {
      console.error("❌ Download all error:", err);
    } finally {
      setLoading((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <button
      className="btn btn-primary fw-normal px-5 py-2 rounded-3 d-inline-flex align-items-center gap-2"
      onClick={handleAll}
      disabled={isLoading}
      style={{ whiteSpace: "nowrap" }}
    >
      {isLoading ? (
        <>
          <span className="spinner-border spinner-border-sm" role="status" />
          Preparing all sheets…
        </>
      ) : (
        <>
          <i className="bi bi-download"></i>
          Download All (.xlsx)
        </>
      )}
    </button>
  );
}

export default Reports;
